import React, { useEffect, useMemo, useRef } from "react";
import { motion } from "framer-motion";
import { FaCheck, FaPlus, FaUndo } from "react-icons/fa";
import Select from "react-select";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";

import { labelledStockTopFields, looseStoneFields } from "../../config/fieldsHelper";
import { handleDropDowns } from "../../config/dropDownHelpers";
import { useAddLooseStoneEntryMutation } from "../../toolkitServices/looseStoneApi";
import LooseStoneItemForm from "./LooseStoneItemForm";

/* ---------- styles ---------- */
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "32px",
    height: "32px",
    borderColor: state.isFocused ? "#A78BFA" : "#D1D5DB",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(167,139,250,0.35)" : "none",
    fontSize: "12px",
    backgroundColor: "white",
  }),
  indicatorsContainer: (b) => ({ ...b, height: "30px" }),
  dropdownIndicator: (b) => ({ ...b, padding: "4px" }),
  clearIndicator: (b) => ({ ...b, padding: "4px" }),
  valueContainer: (b) => ({ ...b, padding: "0 6px", height: "30px" }),
  input: (b) => ({ ...b, margin: 0, padding: 0 }),
  menu: (b) => ({ ...b, fontSize: "12px", zIndex: 50 }),
  menuList: (b) => ({ ...b, maxHeight: "140px", overflowY: "auto" }),
  singleValue: (b) => ({ ...b, fontSize: "12px" }),
};

/* ---------- helpers ---------- */
const num = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  const n = typeof v === "string" ? parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : 0;
};
const fmt3 = (v) => (Math.round(num(v) * 1000) / 1000).toFixed(3);
const fmt0 = (v) => String(Math.round(num(v)));

const rowLooksFilled = (r = {}) =>
  !!(r?.stoneName || r?.stnWt || r?.stnPcs || r?.stnDescription);

const CARD_HEIGHT_CLASS = "h-[40vh] sm:h-[40vh]";

const defaultRow = {
  stoneName: null,
  shapeName: null,
  colorName: null,
  sizeName: null,
  settingTypeName: null,
  stnWt: "",
  stnPcs: "",
  stnDescription: "",
  stnUnit: { value: "gram", label: "gram" },
};

export const defaultLooseStoneValues = {
  vendor: "",
  branch: "",
  counter: "", // 🔥 NEW
  looseStones: [defaultRow],
};
export const defaultLooseDiamondValues = {
  vendor: "",
  branch: "",
  counter: "", // 🔥 NEW
  looseDiamonds: [defaultRow],
};

// components/looseStone/LooseDiamondForm.jsx  (same file jahan defaultLooseDiamondValues defined hai)


/* ---------- inflate server entry -> form ---------- */
export function inflateEntryToForm(entry) {
  if (!entry) return defaultLooseStoneValues;
  return {
    vendor: entry.vendorId
      ? { value: entry.vendorId, label: entry.vendorName || "" }
      : "",
    branch: entry.branchId
      ? { value: entry.branchId, label: entry.branchName || "" }
      : "",
    counter: entry.counterId
      ? { value: entry.counterId, label: entry.counterName || "" }
      : "",
    looseStones: [
      {
        stoneName: entry.stoneId
          ? { value: entry.stoneId, label: entry.stoneName || "" }
          : null,
        shapeName: entry.shape
          ? { value: entry.shape, label: entry.shapeName || "" }
          : null,
        colorName: entry.color
          ? { value: entry.color, label: entry.colorName || "" }
          : null,
        sizeName: entry.size
          ? { value: entry.size, label: entry.sizeName || "" }
          : null,
        settingTypeName: entry.settingType
          ? { value: entry.settingType, label: entry.settingTypeName || "" }
          : null,
        stnWt: entry.wt ?? "",
        stnPcs: entry.pcs ?? "",
        stnDescription: entry.description || "",
        stnUnit: { value: entry.unit || "gram", label: entry.unit || "gram" },
      },
    ],
  };
}

/* ---------- deflate for update (single row) ---------- */
function deflateFormForUpdate(form) {
  const row = (form?.looseStones && form.looseStones[0]) || {};
  return {
    stoneId: row.stoneName?.value || null,
    stoneName: row.stoneName || null,

    shape: row.shapeName || null,
    color: row.colorName || null,
    size: row.sizeName || null,
    settingType: row.settingTypeName || null,

    unit: (row.stnUnit?.value || row.stnUnit?.label || "").toString().toLowerCase(),
    wt: row.stnWt ?? 0,
    pcs: row.stnPcs ?? 0,
    description: row.stnDescription ?? "",

    vendorId: form.vendor?.value || null,
    vendorName: form.vendor?.label || "",
    branchId: form.branch?.value || null,
    branchName: form.branch?.label || "",
    counterId: form.counter?.value || null,   // 🔥 NEW
    counterName: form.counter?.label || "",   // 🔥 NEW
  };
}

const LooseStoneForm = ({ existingData = null, onSubmitOverride, submitLabel }) => {
  const vendors  = useSelector((s) => s.vendors?.vendors);
  const branches = useSelector((s) => s.branches?.branches);
  const counters = useSelector((s) => s.counters?.counters); // 🔥

  const [addEntry, { isLoading: isSavingCreate }] = useAddLooseStoneEntryMutation();
  const isEdit = typeof onSubmitOverride === "function";

  const {
    control,
    handleSubmit,
    register,
    setValue,
    reset,
    getValues,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    shouldUnregister: true,
    defaultValues: existingData
      ? { ...defaultLooseStoneValues, ...existingData }
      : defaultLooseStoneValues,
  });
  const branch = watch("branch")
  const { fields, append, remove } = useFieldArray({ control, name: "looseStones" });

  const listRef = useRef(null);
  const firstFieldRef = useRef(null);
  const rowFirstRefs = useRef({});

  /* keyboard helpers */
  useEffect(() => {
    const onKeys = (e) => {
      if (e.ctrlKey && e.key === "+") {
        if (isEdit) return;
        e.preventDefault();
        append({ ...defaultRow });
        requestAnimationFrame(() =>
          setTimeout(() => focusRowAt((getValues("looseStones")?.length || 1) - 1), 0)
        );
      }
      if (e.ctrlKey && e.key.toLowerCase() === "r") {
        e.preventDefault();
        reset(
          existingData ? { ...defaultLooseStoneValues, ...existingData } : defaultLooseStoneValues
        );
        requestAnimationFrame(() => {
          focusRowAt(0);
          setTimeout(() => firstFieldRef.current?.focus(), 0);
        });
      }
      if (e.ctrlKey && e.key.toLowerCase() === "s") {
        e.preventDefault();
        handleSubmit(onSubmit)();
      }
    };
    window.addEventListener("keydown", onKeys);
    return () => window.removeEventListener("keydown", onKeys);
  }, [append, getValues, handleSubmit, reset, existingData, isEdit]);

  const handleFocusCapture = (e) => {
    const card = e.target.closest(".stock-card");
    if (card) card.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const focusRowAt = (idx) => {
    const cards = listRef.current?.querySelectorAll(".stock-card");
    if (!cards || !cards.length) return;
    const i = Math.max(0, Math.min(idx, cards.length - 1));
    const card = cards[i];
    card?.scrollIntoView({ behavior: "smooth", block: "start" });
    const id = card?.dataset?.fieldId;
    const el = rowFirstRefs.current[id];
    if (el?.focus) el.focus();
  };

  /* 🔥 live totals using useWatch */
  const rowsWatch = useWatch({ control, name: "looseStones" });
  const totals = useMemo(() => {
    const rows = Array.isArray(rowsWatch) ? rowsWatch : [];
    let wt = 0, pcs = 0, itemsValid = 0;
    for (const r of rows) {
      wt  += num(r?.stnWt);
      pcs += num(r?.stnPcs);
      if (rowLooksFilled(r || {})) itemsValid++;
    }
    return { itemsAll: rows.length, itemsValid, wt, pcs };
  }, [rowsWatch]);

  /* submit */
  const onSubmit = async (form) => {
    const rows = Array.isArray(form.looseStones) ? form.looseStones : [];
    const looseStones = rows.filter(rowLooksFilled);
    if (!looseStones.length) {
      toast.error("Add at least one loose stone row.");
      return;
    }

    // EDIT: send single-row body to override
    if (isEdit) {
      const body = deflateFormForUpdate(form);
      try {
        await onSubmitOverride(body);
      } catch {
        /* parent handles toast */
      }
      return;
    }

    // CREATE (multiple rows): call API per row
    const toGram = (wt, unit) => {
      const val = num(wt);
      const u = String(unit || "").toLowerCase();
      if (u === "carat" || u === "ct") return val / 5;
      return val;
    };

    try {
      const payload = []
      for (const r of looseStones) {
        const unit = (r?.stnUnit?.value || r?.stnUnit?.label || "gram").toLowerCase();

        const body = {
          stoneId: r?.stoneName?.value || null,
          stoneName: r?.stoneName || null,

          shape: r?.shapeName || null,
          color: r?.colorName || null,
          size: r?.sizeName || null,
          settingType: r?.settingTypeName || null,

          unit: "gram",
          wt: toGram(r?.stnWt, unit),
          pcs: r?.stnPcs ?? 0,
          description: r?.stnDescription ?? "",

          vendorId: form?.vendor?.value || null,
          vendorName: form?.vendor?.label || "",
          branchId: form?.branch?.value || null,
          branchName: form?.branch?.label || "",
          counterId: form?.counter?.value || null,  // 🔥 include counter in payload
          counterName: form?.counter?.label || "",
        };
        payload.push(body)
      }
      await addEntry(payload).unwrap();

      toast.success("Loose stones saved.");
      reset(defaultLooseStoneValues);
      requestAnimationFrame(() => {
        focusRowAt(0);
        setTimeout(() => firstFieldRef.current?.focus(), 0);
      });
    } catch (err) {
      const msg = err?.data?.msg || err?.error || "Failed to add loose stones";
      toast.error(msg);
    }
  };

  // Top fields to show: vendor / branch / counter
  const topFields = (labelledStockTopFields || []).filter((f) =>
    ["vendor", "branch", "counter"].includes(f?.name)
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
      <motion.div
        initial={{ opacity: 0.9, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className="rounded-xl bg-white overflow-hidden"
      >
        {/* Top: Vendor / Branch / Counter */}
        <div className="bg-white shadow-sm rounded-lg px-3 py-2 border mb-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
            {topFields.map((item, idx) => (
              <div key={idx} className="min-w-0">
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  {item?.label}{" "}
                  {item?.mandatory && <span className="text-red-500">*</span>}
                </label>

                {["text", "number", "date"].includes(item?.type) && (
                  <>
                    <input
                      type={item?.type === "number" ? "number" : item?.type || "text"}
                      {...register(item.name, {
                        required: item?.mandatory ? `${item.label} is required` : false,
                      })}
                      placeholder={`Enter ${item.label}`}
                      className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                    />
                    {errors[item.name] && (
                      <p className="text-red-500 text-[11px] mt-1">
                        {errors[item.name].message}
                      </p>
                    )}
                  </>
                )}

                {item?.type === "dropdown" && (
                  <>
                    <Controller
                      name={item.name}
                      control={control}
                      rules={{
                        required: item?.mandatory ? `${item.label} is required` : false,
                      }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={handleDropDowns({
                            key: item["name"],
                            vendors,
                            branches,
                            counters, // 🔥 pass counters to dropdown helper
                            branch
                          })}
                          styles={selectStyles}
                          classNamePrefix="react-select"
                          placeholder={`Select ${item.label}`}
                          isClearable
                          menuPlacement="auto"
                        />
                      )}
                    />
                    {errors[item.name] && (
                      <p className="text-red-500 text-[11px] mt-1">
                        {errors[item.name].message}
                      </p>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Rows */}
        <div className="bg-white shadow-sm rounded-xl p-0">
          <div
            ref={listRef}
            onFocusCapture={handleFocusCapture}
            className={`relative ${CARD_HEIGHT_CLASS} overflow-y-auto scroll-smooth snap-y snap-mandatory`}
          >
            {fields.map((f, rowindex) => (
              <LooseStoneItemForm
                key={f.id}
                fieldId={f.id}
                control={control}
                register={register}
                errors={errors}
                rowindex={rowindex}
                setValue={setValue}
                firstFieldRef={rowindex === 0 ? firstFieldRef : undefined}
                registerFirstField={(id, el) => {
                  if (el) rowFirstRefs.current[id] = el;
                }}
                looseStoneFields={looseStoneFields}
                cardHeightClass={CARD_HEIGHT_CLASS}
                onDelete={() => {
                  const targetIdx = Math.max(0, rowindex - 1);
                  remove(rowindex);
                  requestAnimationFrame(() =>
                    setTimeout(() => {
                      const cards = listRef.current?.querySelectorAll(".stock-card");
                      if (cards?.length) {
                        const i = Math.max(0, Math.min(targetIdx, cards.length - 1));
                        cards[i]?.scrollIntoView({ behavior: "smooth", block: "start" });
                      }
                    }, 0)
                  );
                }}
              />
            ))}
          </div>

          {/* Summary (LIVE) */}
          <div className="border rounded-lg bg-slate-50/70 px-3 py-2 mt-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[12px] font-semibold text-slate-600">Summary</span>
              <span className="text-[11px] text-slate-500">
                Items: <b>{totals.itemsValid}</b> / {totals.itemsAll}
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
              <SummaryChip label="Total Pcs" value={fmt0(totals.pcs)} />
              <SummaryChip label="Total Wt" value={fmt3(totals.wt)} />
            </div>
          </div>

          {/* Actions */}
          <div className="p-2 flex flex-wrap gap-2 justify-between">
            {!isEdit && (
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                type="button"
                className="inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white py-2 px-4 rounded-md text-sm font-medium shadow-sm"
                onClick={() => {
                  append({ ...defaultRow });
                  requestAnimationFrame(() =>
                    setTimeout(() => focusRowAt((getValues("looseStones")?.length || 1) - 1), 0)
                  );
                }}
              >
                <FaPlus className="text-sm" />
                Add Row (Ctrl + +)
              </motion.button>
            )}

            <div className="flex gap-2 ml-auto">
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                type="submit"
                disabled={isEdit ? false : isSavingCreate}
                className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white py-2 px-4 rounded-md text-sm font-semibold shadow-sm"
              >
                <FaCheck className="text-sm" />
                {submitLabel || (isSavingCreate ? "Saving..." : "Save (Ctrl + S)")}
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                type="button"
                onClick={() => {
                  reset(
                    existingData ? { ...defaultLooseStoneValues, ...existingData } : defaultLooseStoneValues
                  );
                  requestAnimationFrame(() => {
                    focusRowAt(0);
                    setTimeout(() => firstFieldRef.current?.focus(), 0);
                  });
                }}
                className="inline-flex items-center gap-2 bg-slate-700 hover:bg-slate-800 text-white py-2 px-4 rounded-md text-sm font-semibold shadow-sm"
              >
                <FaUndo className="text-sm" />
                Reset (Ctrl + R)
              </motion.button>
            </div>
          </div>
        </div>
      </motion.div>
    </form>
  );
};

/* tiny chip */
const SummaryChip = ({ label, value }) => (
  <div className="rounded-lg border bg-white px-3 py-2 shadow-sm">
    <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
    <div className="text-sm font-semibold text-slate-800">{value}</div>
  </div>
);

export default LooseStoneForm;
